/**
 * "Convites individuais". build-prompt sections 6 and 8.
 *
 * One row per family, each with a ready WhatsApp message. The browser cannot
 * confirm delivery, so "enviado" means "I opened WhatsApp" and nothing more.
 * An actual answer is what flips a row to "respondeu".
 */

import { useEffect, useState } from 'react';
import { host, type HouseholdRow } from '../api.js';
import { copyText } from './copy.js';
import { AddHousehold, HouseholdEditor } from './HouseholdEditor.js';

export function Invites({ eventId }: { eventId: string }) {
  const [rows, setRows] = useState<HouseholdRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  // Arriving from the dashboard with a family already chosen.
  useEffect(() => {
    const asked = new URLSearchParams(window.location.search).get('abrir');
    if (asked) setEditing(asked);
  }, []);

  useEffect(() => {
    host
      .households(eventId)
      .then(({ households }) => setRows(households))
      .catch((err: Error) => setError(err.message));
  }, [eventId]);

  if (error) {
    return (
      <div className="page">
        <div className="banner error">{error}</div>
      </div>
    );
  }

  if (!rows) return <div className="spinner">Carregando…</div>;

  async function send(row: HouseholdRow) {
    // Optimistic, because the browser hands off to WhatsApp and never hears
    // back. Marking it after the fact would mean she taps and nothing changes.
    setRows((current) =>
      current === null
        ? current
        : current.map((r) =>
            r.householdId === row.householdId
              ? { ...r, sentAt: new Date().toISOString() }
              : r,
          ),
    );
    window.open(row.whatsappUrl, '_blank', 'noopener');
    await host.markSent(eventId, row.householdId).catch(() => undefined);
  }

  const groups = new Map<string, HouseholdRow[]>();
  for (const row of rows) {
    const key = row.source ?? 'Sem grupo';
    groups.set(key, [...(groups.get(key) ?? []), row]);
  }

  return (
    <div className="page">
      <a href={`/painel/${eventId}`}>← Painel</a>
      <h1>Convites</h1>
      <p className="muted">
        Um link por família. “WhatsApp” abre a conversa com a mensagem pronta,
        “Copiar” pega só o link, e “Editar” mostra quem está no convite.
      </p>

      {[...groups].map(([source, group]) => (
        <div key={source}>
          <h2>
            {source} ({group.length})
          </h2>
          <div className="card">
            {group.map((row) => (
              <div key={row.householdId} className="invite-row">
                <div className="invite-head">
                  <div className="grow">
                    <div className="label-main">{row.label}</div>
                    <div className="muted">
                      {row.people.length} pessoa{row.people.length === 1 ? '' : 's'}
                      {/*
                        * Opened matters on its own: it is the difference
                        * between "the link is broken" and "they have not got
                        * round to it".
                        */}
                      {row.respondedAt
                        ? ' · respondeu'
                        : row.openedAt
                          ? ' · abriu, sem responder'
                          : row.sentAt
                            ? ' · enviado'
                            : ''}
                    </div>
                  </div>
                </div>

                {/*
                  * The three actions get their own line.
                  *
                  * Beside the name they squeezed it to "A…" on a 390px screen:
                  * three buttons plus a family name do not fit across a phone,
                  * and the name is the only way she knows which row is which.
                  */}
                <div className="invite-actions">
                  <button
                    onClick={() =>
                      setEditing(editing === row.householdId ? null : row.householdId)
                    }
                    aria-expanded={editing === row.householdId}
                  >
                    {editing === row.householdId ? 'Fechar' : 'Editar'}
                  </button>
                  <button
                    onClick={async () => {
                      const ok = await copyText(row.invitationUrl);
                      setCopied(ok ? row.householdId : null);
                      setTimeout(() => setCopied(null), 2000);
                    }}
                    title="Copiar o link desta família"
                  >
                    {copied === row.householdId ? 'Copiado' : 'Copiar'}
                  </button>
                  <button className="primary" onClick={() => send(row)}>
                    {row.sentAt ? 'Reenviar' : 'WhatsApp'}
                  </button>
                </div>

                {editing === row.householdId && (
                  <HouseholdEditor
                    eventId={eventId}
                    row={row}
                    onChanged={(next) =>
                      setRows((current) =>
                        (current ?? []).map((r) =>
                          r.householdId === next.householdId ? next : r,
                        ),
                      )
                    }
                    onRemoved={() => {
                      setRows((current) =>
                        (current ?? []).filter(
                          (r) => r.householdId !== row.householdId,
                        ),
                      );
                      setEditing(null);
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      <AddHousehold
        eventId={eventId}
        onAdded={(row) => {
          setRows((current) => [...(current ?? []), row]);
          setEditing(row.householdId);
        }}
      />

      {preview && (
        <div className="card">
          <strong>Mensagem</strong>
          <pre
            className="scroll-x"
            style={{ whiteSpace: 'pre-wrap', font: 'inherit', marginTop: '0.5rem' }}
          >
            {preview}
          </pre>
          <button className="block" onClick={() => setPreview(null)}>
            Fechar
          </button>
        </div>
      )}
    </div>
  );
}
